import { css } from '@emotion/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from 'redux/store';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from 'react-redux';
import t from 'react-translate';

// Components
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import PracticeSubmission from 'practice_room/components/shared/practice-submission';
import usePrevious from 'shared/hooks/use-previous';

import { RootState } from 'redux/schemas';
import { VideoPracticeScenario, VideoPracticeSubmission } from 'redux/schemas/models/video-practice';
import { getSubmission } from 'redux/selectors/video-practice';
import { nextAndPrevSubmissions } from 'redux/selectors/video-practice-feedback';
import { getComments, getPracticeSubmission, getScenario, resetPracticeComments } from 'redux/actions/video-practice';
import { setShowFeedbackInstruction } from 'redux/actions/video-practice-feedback';

import { SubmissionPreview, SubmittedUser } from 'practice_room/components/submission-overview/submission-overview';
import VideoPracticeOverview from 'practice_room/components/scenario-overview/video-practice-overview';
import SubmissionSlider from 'practice_room/components/shared/user-submission-slider';
import { handheld, screenMdMax } from 'styles/global_defaults/media-queries';
import { extraLargeSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '../../../../../config/pendo.config.json';;

const width = (isLargeScreen) => css`
  ${isLargeScreen
    ? 'width: 1040px !important;'
    : 'max-width: 1040px;'
}`;

const styles = (isLargeScreen) => css`
  ${width(isLargeScreen)}
  .overview-container {
    max-width: 800px;
    min-height: 600px;
  }
  .header {
    ${width(isLargeScreen)}
    max-height: 165px !important;
    top: ${extraLargeSpacing}px !important;
  }
  .comment {
    /*rtl:ignore*/
    right: ${2 * extraLargeSpacing}px;
    ${handheld(css`
      right: 5%;
    `)}
  }
`;

interface PracticeFeedbackOverviewProps {
  submissionId: number
  scenarioId: number
}

const PracticeFeedbackOverview = ({ submissionId, scenarioId }: PracticeFeedbackOverviewProps) => {
  const isLargeScreen = useMediaQuery({
    query: `(min-width: ${screenMdMax}px)`,
  });
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(submissionId);

  const scenario = useSelector<RootState, VideoPracticeScenario>(state => state.models.practiceScenarios[scenarioId]);
  const submission = useSelector<RootState, VideoPracticeSubmission>(state => getSubmission(state, selectedSubmissionId));
  const commentsLoaded = useSelector<RootState, boolean>(
    state => state.app.videoPracticeSubmissions?.[selectedSubmissionId]?.commentsOnceLoaded,
  );
  const { next, prev } = useSelector<RootState, SubmissionPreview>(
    state => nextAndPrevSubmissions(state, selectedSubmissionId),
  );
  const showFeedbackInstruction = useSelector<RootState, boolean>(
    state => state.app.videoPracticeFeedback.showFeedbackInstruction,
  );
  const feedbackCriteriaId = useSelector<RootState, boolean>(
    state => state.app.videoPracticeFeedback.feedbackCriteriaId,
  );
  const practiceFeedbackRef = useRef();

  const dispatch = useAppDispatch();

  const prevSubmissionId = usePrevious(selectedSubmissionId);

  useEffect(() => {
    if (selectedSubmissionId && scenarioId) {
      if (!submission) {
        dispatch(getPracticeSubmission({
          submissionId: selectedSubmissionId,
          scenarioId,
        }));
      }
    }
  }, [dispatch, scenarioId, selectedSubmissionId, submission]);

  useEffect(() => {
    if (!scenario) {
      dispatch(getScenario({ scenarioId }));
    }
  }, [dispatch, scenario, scenarioId]);

  useEffect(() => {
    if (submission && selectedSubmissionId && feedbackCriteriaId && !commentsLoaded) {
      dispatch(getComments({ submissionId: selectedSubmissionId, feedbackCriteriaId }));
    }
  }, [commentsLoaded, dispatch, feedbackCriteriaId, selectedSubmissionId, submission]);

  useEffect(() => {
    // Reset comments on exiting submission view, to get latest data
    if (prevSubmissionId) {
      dispatch(resetPracticeComments({ submissionId: prevSubmissionId }));
    }
  }, [dispatch, prevSubmissionId]);

  const onSlide = useCallback((id: number) => {
    setSelectedSubmissionId(id);
    if (showFeedbackInstruction && selectedSubmissionId) {
      dispatch(setShowFeedbackInstruction({ show: false }));
    }
  }, [dispatch, selectedSubmissionId, showFeedbackInstruction]);

  return (
    <div
      className={`my-7 bg-white ${isLargeScreen ? 'mx-auto' : ''}`}
      css={styles(isLargeScreen)}
      ref={practiceFeedbackRef}
    >
      <LoadingWrapper
        isLoaded={!!scenario && !!submission}
        loaderType={LoaderType.PLACEHOLDER}
        className='overview-container w-100 mx-auto my-6'
        keepRendered={false}
      >
        <VideoPracticeOverview isPracticeFeedback scenarioId={scenarioId}>
          <SubmissionSlider
            isNext={!!next}
            isPrev={!!prev}
            onNext={() => onSlide(next.id)}
            onPrev={() => onSlide(prev.id)}
            prevPreview={prev?.videoFile?.thumbnailUrl ?? ''}
            nextPreview={next?.videoFile?.thumbnailUrl ?? ''}
            showPopover={!!next}
            popoverContent={(
              <div className='text-small'>
                {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.INSTRUCTION.NEXT()}
              </div>
            )}
            dataQaNext={config.pendo.practiceFeedback.nextSubmission}
            dataQaPrev={config.pendo.practiceFeedback.prevSubmission}
          >
            <div className='overview-container d-flex justify-content-center mb-6 mx-auto'>
              {submission && (
                <div className='d-flex flex-column w-100'>
                  <div className='border-bottom border-gray-4 py-4 mb-5'>
                    <SubmittedUser
                      user={submission?.user}
                      isTeamMember={submission.isTeamMember}
                      teamName={submission.teamName}
                    />
                  </div>
                  <PracticeSubmission
                    submissionId={selectedSubmissionId}
                    showSubmissionOptions={false}
                    isPracticeFeedback
                    scenarioId={scenarioId}
                  />
                </div>
              )}
            </div>
          </SubmissionSlider>
        </VideoPracticeOverview>
      </LoadingWrapper>
    </div>
  );
};

export default PracticeFeedbackOverview;
